import { Card, CardContent, CardHeader } from '@/app/common/components/Card.tsx'
import { Button } from '@/app/common/components/button/Button.tsx'
import { Input } from '@/app/common/components/input/Input.tsx'
import Label from '@/app/common/components/Label'
import AutoDistributionCard from '../shared/AutoDistributionCard'
import DistributionRulesCard from '../shared/DistributionRulesCard'
import { GraduationCap } from 'lucide-react'
import { cn } from '@/lib/utils'
import { ConfirmChangeRuleDialog } from '../shared/ChangeRuleDialog'
import { useState } from 'react'
import z from 'zod'
import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import FormErrorMessage from '@/common/components/FormErrorMessage.tsx'
import {
  GradeAwardPayload,
  useUpdateGradeDistributions,
} from '@/queries/purse_configs/useUpdateGradeDistributions.ts'
import { useToast } from '@/shad/ui/toast/use-toast.ts'
import { useQueryClient } from '@tanstack/react-query'
import { PURSE_KEYS } from '@/queries/program_purses/keys.ts'
import { PartialPurseData } from '@/pages/program_purse_settings/purse-details/_components/distribution-settings-options/types.ts'
import {
  ModifierCode,
  ProgramPurseResource,
} from '@/queries/program_purses/schemas.ts'
import { useUpdateProgramPurse } from '@/queries/purse_configs/useUpdateProgramPurse.ts'
import SpecialNeedsAwardCard from '../shared/SpecialNeedsAwardCard'
import { ExtraModifierCards } from '../shared/ExtraModifierCard'
import { ExtraModifierData } from '../modifiers/types'

// Form schema for validation
const gradeConfigSchema = z.object({
  gradeConfigs: z.record(
    z.string(),
    z.coerce.number().min(0, 'Amount must be greater than or equal to 0')
  ),
})
type GradeConfigFormData = z.infer<typeof gradeConfigSchema>

type UIVariant = 'form_view' | 'all_settings_view'

type Props = {
  variant: UIVariant
  partialPurseData: PartialPurseData
  gradeDistributionRules: ProgramPurseResource['distributions_rules']['grade_distributions']
  onBack?: () => void
  onSuccessfulSave: () => void
  onChangeRule?: () => void
  onNavigateToSpecialNeedsModifier: () => void
  extraModifierData?: ExtraModifierData
  onConfigureModifier?: (code: ModifierCode) => void
}

export default function GradeLevelRuleDetails({
  variant,
  partialPurseData,
  gradeDistributionRules,
  // navigation buttons
  onBack, // configure → back to chooser
  onSuccessfulSave, // configure → go to success
  onChangeRule, // view → go to chooser
  onNavigateToSpecialNeedsModifier,
  extraModifierData,
  onConfigureModifier,
}: Props) {
  const isView = variant === 'all_settings_view'
  const [confirmOpen, setConfirmOpen] = useState(false)
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const gradeDistributionsMutation = useUpdateGradeDistributions()
  const updatePurseMutation = useUpdateProgramPurse()
  const isSubmitting =
    gradeDistributionsMutation.status === 'pending' ||
    updatePurseMutation.status === 'pending'

  // Initialize form with current grade config amounts
  const defaultValues: GradeConfigFormData = {
    gradeConfigs: gradeDistributionRules.reduce(
      (acc, gradeConfig) => {
        acc[gradeConfig.id] = gradeConfig.amount || 0
        return acc
      },
      {} as Record<string, number>
    ),
  }

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<GradeConfigFormData>({
    resolver: zodResolver(gradeConfigSchema),
    values: defaultValues,
  })

  const handleSaveGradeConfigs = async (data: GradeConfigFormData) => {
    updatePurseMutation.mutate(
      {
        params: {
          purse_id: partialPurseData.id,
          program_id: partialPurseData.programId,
        },
        payload: { awarding_method_code: 'grade_level' },
      },
      {
        onError: (error) => {
          toast({
            title: 'Error',
            description:
              error.message || 'Failed to update grade award brackets',
          })
        },
      }
    )

    const gradeAwards: GradeAwardPayload = Object.entries(
      data.gradeConfigs
    ).map(function ([id, amount]) {
      return {
        id: Number(id),
        amount,
      }
    })

    gradeDistributionsMutation.mutate(
      {
        params: {
          programId: partialPurseData.programId,
          purseId: partialPurseData.id,
        },
        payload: gradeAwards,
      },
      {
        onSuccess: () => {
          // Reset the form dirty state after a successful save
          reset(data)
          queryClient.invalidateQueries({
            queryKey: PURSE_KEYS.PURSE_DETAILS(
              partialPurseData.id.toString(),
              partialPurseData.programId
            ),
          })
          queryClient.invalidateQueries({
            queryKey: PURSE_KEYS.PURSES(partialPurseData.programId),
          })
          toast({
            title: 'Success',
            description: 'Grade award brackets updated successfully',
          })
          onSuccessfulSave()
        },
        onError: (error) => {
          toast({
            title: 'Error',
            description:
              error.message || 'Failed to update grade award brackets',
          })
        },
      }
    )
  }

  return (
    <div className="w-full px-3 py-6 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-3xl space-y-6">
        {isView && (
          <>
            <AutoDistributionCard partialPurseData={partialPurseData} />
            <DistributionRulesCard />
          </>
        )}

        {isView && (
          <Card>
            <CardHeader>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <GraduationCap className="h-6 w-6" aria-hidden />
                  <div className="mt-2 text-sm font-medium text-gray-900">
                    Grade Level Configuration
                  </div>
                  <p className="mt-1 text-xs text-gray-600">
                    Set different award amounts for each grade level (K-12)
                  </p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setConfirmOpen(true)}
                >
                  Change Rule
                </Button>

                <ConfirmChangeRuleDialog
                  open={confirmOpen}
                  onOpenChange={setConfirmOpen}
                  onConfirm={onChangeRule ?? (() => {})}
                />
              </div>
            </CardHeader>
          </Card>
        )}

        <form
          id="purse-grade-distributions-form"
          onSubmit={handleSubmit(handleSaveGradeConfigs)}
        >
          <Card>
            <CardHeader>
              <div>
                <h3 className="text-base sm:text-lg font-semibold text-gray-900">
                  Grade Level Configuration
                </h3>
                {!isView && (
                  <p className="mt-1 text-sm text-gray-700">
                    Set the scholarship amount for each grade level.
                  </p>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3">
                {gradeDistributionRules.map((gradeConfig) => (
                  <div key={gradeConfig.id} className="space-y-1.5">
                    <Label
                      htmlFor={gradeConfig.grade_level_name}
                      weight="medium"
                    >
                      {gradeConfig.grade_level_name}
                    </Label>
                    <div className="relative">
                      <span
                        aria-hidden
                        className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-500"
                      >
                        $
                      </span>
                      <Controller
                        name={`gradeConfigs.${gradeConfig.id}`}
                        control={control}
                        render={({ field }) => (
                          <Input
                            {...field}
                            id={gradeConfig.grade_level_name}
                            type="number"
                            inputMode="decimal"
                            min={0}
                            step="0.01"
                            className="pl-6"
                            placeholder="0"
                            onWheel={(e) =>
                              (e.currentTarget as HTMLInputElement).blur()
                            }
                          />
                        )}
                      />
                      {errors.gradeConfigs && (
                        <FormErrorMessage
                          message={errors.gradeConfigs[gradeConfig.id]?.message}
                        />
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div
                className={cn(
                  'mt-6 flex gap-3',
                  isView ? 'justify-start' : 'justify-end'
                )}
              >
                {isView ? (
                  <Button
                    type="submit"
                    form="purse-grade-distributions-form"
                    variant="primary"
                    size="sm"
                    disabled={!isDirty || isSubmitting}
                  >
                    {isSubmitting ? 'Saving...' : 'Save Changes'}
                  </Button>
                ) : (
                  <>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={onBack}
                    >
                      Back
                    </Button>
                    <Button
                      type="submit"
                      form="purse-grade-distributions-form"
                      variant="primary"
                      size="sm"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Saving...' : 'Save Rules'}
                    </Button>
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        </form>
        {isView && (
          <>
            <SpecialNeedsAwardCard
              onConfigure={onNavigateToSpecialNeedsModifier}
            />
            {extraModifierData && onConfigureModifier && (
              <ExtraModifierCards
                modifiers={extraModifierData}
                onConfigureModifier={onConfigureModifier}
              />
            )}
          </>
        )}
      </div>
    </div>
  )
}
